/**
 * Copyright (c) 2026 Constructor Technology AG. All rights reserved.
 *
 * Proprietary and confidential. Unauthorized copying, distribution, or use
 * is prohibited.
 *
 * SPDX-License-Identifier: LicenseRef-Constructor-Proprietary
 */

import * as apiRaceimages from '@/api/raceimages'
import { postComparesRaceImages } from '../../../api/compares'
import router from "../../../router";
import * as types from './mutation-types';
import { getDefaultColumns } from './state';

export default {
  resetRaceContextMenu({ commit }) {
    commit(types.RESET_CONTEXT_MENU);
  },
  setRaceContextMenu({ commit }, payload) {
    commit(types.SET_CONTEXT_MENU, payload);
  },
  setCurrentRace({ commit }, payload) {
    commit('setCurrentRace', payload)
  },
  addImageToCompares({ commit, getters }, payload) {
    const imgId = getters.getCurrentBaselineComparesImages
    const isShowComparesFooter = getters.getIsShowComparesFooter
    if (!imgId) {
      commit('addImageToBaselineCompares', payload)
    } else {
      commit('addImageToCompares', payload)
    }

    if (!isShowComparesFooter) {
      commit(types.OPEN_COMPARE_PANEL)
    }
  },
  removeImageFromCompares({ commit }, payload) {
    commit('removeImageFromCompares', payload.id)
  },
  submitImagesToCompares({ commit, getters }) {
    const imagesIdsList = getters.getComparesImagesByCurrentRaceId.map(img => img.id)
    const raceId = getters.getCurrentRaceId
    const first_image_id = getters.getCurrentBaselineComparesImages ? getters.getCurrentBaselineComparesImages.id : undefined
    const data = {
      comparisons: imagesIdsList,
      race_id: raceId,
      first_image_id
    }
    postComparesRaceImages(data)
     .then( res => {
       router.push({ name: 'compare', params: { compareId: res.data.id } })
       return commit('removeImagesFromCompares', raceId)
     })
     .catch(e => console.log(e))
  },
  setToBaseline({ commit, getters }, payload) {
    const imgList = [...getters.getCurrentBaselineComparesImagesAll]
    const image = getters.getCurrentBaselineComparesImages
    const index = imgList.findIndex(img => img.id === image.id)
    imgList[index] = payload
    commit('updateImageBaselineCompare', imgList)
  },
  clearRaceComparesImages({ commit, getters }) {
    const raceId = getters.getCurrentRaceId
    commit('removeImagesFromCompares', raceId)
  },
  changeOrdered({ commit }, payload) {
    commit('changeOrdered', payload)
  },
  changeIsShowComparesFooter({ commit }) {
    commit('changeIsShowComparesFooter')
  },
  clearFiltersForServer({ dispatch }, payload) {
    const { columnId, raceId } = payload
    dispatch('updateColumn', {
      columnId,
      filtersForServer: {},
      imageList: [],
      page: 1,
      totalImagesFiltered: 0,
      totalImagesNew: 0,
    })
    dispatch('getImages', {
      columnId,
      page: 1,
      raceId,
    })
  },
  async getImages({ dispatch, getters }, payload) {
    const { columnId, direction = 'end', page, raceId } = payload
    try {
      const updatedColumns = getters.columns.find(el => el.id === columnId) || {}
      dispatch('updateColumn', { columnId, loading: true })

      const { data } = await apiRaceimages.getRaceImages(raceId, {
        limit: 40,
        order: updatedColumns.filtersForUI?.order,
        page,
        ...updatedColumns.filtersForServer,
      })

      const curImages = updatedColumns?.imageList || []
      const newImages = data.images
      const updatedImagesList = direction === 'end' ? [...curImages, ...newImages] : [...newImages, ...curImages]
      dispatch('updateColumn', {
        columnId,
        imageList: updatedImagesList,
        loading: false,
        page,
        totalImagesNew: 0,
        totalImagesFiltered: data.filtered_count,
      })
    } catch (e) {
      dispatch('notification/error', 'Failed to retrieve the image list.', { root: true })
      dispatch('updateColumn', {
        columnId,
        loading: false,
      })
    }
  },
  async pollingImages({ commit, dispatch, getters }, raceId) {
    try {
      const pollingList = getters.pollingColumns.map(columnId => {
        const column = getters.columns.find(({ id }) => id === columnId) || {}
        return apiRaceimages.getRaceImages(raceId, {
          limit: 10,
          page: 1,
          ...column.filtersForServer,
        })
      })
      const newImages = await Promise.all(pollingList)

      const updatedColumns = getters.columns.map(column => {
        const columnIndex = getters.pollingColumns.findIndex(id => id === column.id)
        if (columnIndex < 0) {
          return column
        }

        const newFilteredCount = newImages[columnIndex]?.data?.filtered_count || 0
        const prevFilteredCount = column.totalImagesFiltered || 0
        const totalImagesNew = newFilteredCount - prevFilteredCount

        if (totalImagesNew <= 0) {
          return column
        }

        return {
          ...column,
          totalImagesNew,
        }
      })
      commit(types.UPDATE_COLUMNS, updatedColumns)
    } catch (e) {
      dispatch('notification/error', 'Failed to retrieve new images.', { root: true })
    }
  },
  pollingImagesClear({ commit }) {
    commit(types.POLLING_CLEAR)
  },
  pollingImagesInit({ commit, dispatch }, raceId) {
    if (!raceId) {
      return
    }
    const polling = setInterval(() => dispatch('pollingImages', raceId), 30000)
    commit(types.POLLING_INIT, polling)
  },
  resetColumns({ commit }) {
    commit(types.UPDATE_COLUMNS, getDefaultColumns())
  },
  setColumnsFromStore({ commit, getters }, raceId) {
    const persistedColumns = getters.savedRaces && getters.savedRaces[raceId]
    if (!persistedColumns) {
      return
    }

    const updatedColumns = getters.columns.map(column => {
      const savedFilters = persistedColumns.find(({ id }) => id === column.id) || {}
      return {
        ...column,
        ...savedFilters,
      }
    })
    commit(types.UPDATE_COLUMNS, updatedColumns)
  },
  updateColumnsInStore({ commit, getters }, payload) {
    if (!payload.raceId) {
      return
    }

    const result = {
      ...getters.savedRaces,
      [payload.raceId]: payload.columns.map(el => ({
        filtersForServer: el.filtersForServer,
        filtersForUI: el.filtersForUI,
        id: el.id,
      })),
    }
    commit(types.UPDATE_SAVED_RACES, result)
  },
  updateColumn({ commit, getters }, { columnId, ...options }) {
    const updatedColumns = getters.columns.map(el => {
      if (el.id === columnId) {
        return {
          ...el,
          ...options,
        }
      }
      return el
    })
    commit(types.UPDATE_COLUMNS, updatedColumns)
  },
  updateColumnsDialogVisibility({ commit }, payload) {
    commit(types.UPDATE_COLUMNS_DIALOG_VISIBILITY, payload)
  },
  setInitialPollingList({ commit, getters }) {
    const updatedPolling = getters.columns.reduce((res, cur) => {
      if (cur?.filtersForUI?.showColumn) {
        res.push(cur.id)
      }
      return res
    }, [])
    commit(types.POLLING_UPDATE_COLUMNS, updatedPolling)
  },
  updatePollingList({ commit, getters }, { columnId, value }) {
    const updatedPolling = value
      ? [...getters.pollingColumns, columnId]
      : getters.pollingColumns.filter(id => id !== columnId)
    commit(types.POLLING_UPDATE_COLUMNS, updatedPolling)
  },
  updateColumnVisibility({ dispatch }, payload) {
    const { columnId, raceId, value } = payload
    dispatch('updateFiltersForUI', {
      columnId,
      filter: 'showColumn',
      value,
    })

    if (value) {
      dispatch('getImages', {
        columnId,
        page: 1,
        raceId,
      })
    } else {
      dispatch('updateColumn', {
        columnId,
        imageList: [],
        page: 1,
        totalImagesFiltered: 0,
        totalImagesNew: 0,
      })
    }
    dispatch('updatePollingList', { columnId, value })
  },
  updateFiltersForServer({ dispatch }, { columnId, value }) {
    dispatch('updateColumn', {
      columnId,
      filtersForServer: value,
    })
  },
  updateFiltersForUI({ dispatch, getters }, { columnId, filter, value }) {
    const updatedColumns = getters.columns.find(el => el.id === columnId) || {}
    dispatch('updateColumn', {
      columnId,
      filtersForUI: {
        ...updatedColumns.filtersForUI,
        [filter]: value,
      },
    })
  },
}
